import React, { useEffect, useState } from 'react';
import ModalPanel from '../../../components/Modals/ModalPanel';
import type { Annotation } from '../../../features/annotation';
import type { VinInspectionItem } from '../inspectionTypes';
import type { StillFrameResponse } from '../../../hooks/useStillFrame';
import InspectionImageContent from './InspectionImageContent';
import InspectionVideoContent from './InspectionVideoContent';
import type { InspectionMediaState } from '../inspectionMediaTypes';
import { useVideoModalWidth } from '../hooks/useVideoModalWidth';
import { FULL_MEDIA_MODAL_WIDTH } from '../hooks/videoModalWidth';

const sanitizeFileNamePart = (value?: string | number | null) => {
  const sanitized = String(value ?? '')
    .trim()
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '');

  return sanitized || undefined;
};

const getUrlExtension = (url?: string | null) => {
  const path = String(url ?? '').split('?')[0];
  const extension = path.match(/\.([a-z0-9]{2,5})$/i)?.[1];

  return extension ? `.${extension}` : undefined;
};

const getInspectionDownloadFileName = (viewName?: string | null, vin?: string | null, extension?: string) => {
  const view = sanitizeFileNamePart(viewName) || 'view';
  const vinPart = sanitizeFileNamePart(vin) || 'vin';

  return `${view}_${vinPart}${extension || ''}`;
};

interface InspectionMediaModalProps {
  open: boolean;
  onClose: () => void;
  modalState: InspectionMediaState;
  inspection: VinInspectionItem;
  annotations: Annotation[];
  setAnnotations: React.Dispatch<React.SetStateAction<Annotation[]>>;
  createStillFrame: (camera: string, frameTimestamp: string) => Promise<StillFrameResponse>;
  downloadAsset: (url: string, fileName?: string) => Promise<void>;
  refreshAfterAnnotationChange: () => Promise<void> | void;
  disableAnnotation?: boolean;
  activeTab?: string;
  onCreateSuccess?: () => void;
  onImageResolved?: (info: { imageFileId: string; imageURI?: string }) => void;
}

const InspectionMediaModal: React.FC<InspectionMediaModalProps> = ({
  open,
  onClose,
  modalState,
  inspection,
  annotations,
  setAnnotations,
  createStillFrame,
  downloadAsset,
  refreshAfterAnnotationChange,
  disableAnnotation = false,
  activeTab,
  onCreateSuccess,
  onImageResolved,
}) => {
  const imageData = modalState.imageData;
  const baseDownloadFileName = getInspectionDownloadFileName(imageData?.title, inspection?.vin);
  const [videoAspectRatio, setVideoAspectRatio] = useState<number | null>(null);
  const isVideoModalOpen = open && modalState.type === 'video';
  const videoModalWidth = useVideoModalWidth(isVideoModalOpen, videoAspectRatio);

  useEffect(() => {
    setVideoAspectRatio(null);
  }, [modalState.sessionKey]);

  const renderContent = () => {
    if (!open || !imageData) {
      return null;
    }

    if (modalState.type === 'video') {
      return (
        <InspectionVideoContent
          key={`video-${modalState.sessionKey}`}
          src={imageData.src}
          onClose={onClose}
          createStillFrame={createStillFrame}
          downloadAsset={downloadAsset}
          downloadFileName={getInspectionDownloadFileName(imageData?.title, inspection?.vin, getUrlExtension(imageData.src) || '.mp4')}
          onAspectRatioChange={setVideoAspectRatio}
        />
      );
    }
    return (
      <InspectionImageContent
        key={`image-${modalState.sessionKey}`}
        inspectionId={inspection?.inspectionId}
        imageData={imageData}
        annotations={annotations}
        setAnnotations={setAnnotations}
        onClose={onClose}
        downloadFileName={`${baseDownloadFileName}${getUrlExtension(imageData.imageURI ?? imageData.src) || '.jpg'}`}
        downloadAsset={downloadAsset}
        refreshAfterAnnotationChange={refreshAfterAnnotationChange}
        disableAnnotation={disableAnnotation}
        activeTab={activeTab}
        onCreateSuccess={onCreateSuccess}
        onImageResolved={onImageResolved}
      />
    );
  };

  return (
    <ModalPanel open={open} onClose={onClose} width={modalState.type === 'video' ? videoModalWidth : FULL_MEDIA_MODAL_WIDTH}>
      {renderContent()}
    </ModalPanel>
  );
};

export default InspectionMediaModal;
